import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import fallbackImage from '@/core/assets/img/background-default_grey.png';
import { HlsVideoPreview } from './HlsVideoPreview';
import { ReplyModal } from './ReplyModal';
import {
  BdsButton,
  BdsTooltip,
  BdsIcon,
  BdsTypo,
} from '@/core/constants/webComponents';
import { Post } from '@/core/services/interfaces/PostInterfaces';
import { toSafeMarkdownHtml } from '@/core/utils/toSafeMarkdownHtml';
import { parseImageText } from '@/core/utils/parseImageText';
import { isHlsSource, resolveAudioUrl, resolveVideoMimeType, resolveVideoUrl } from '@/core/utils/resolvePostMediaUrl';

function resolveHlsThumbnail(post: Post, imageUrl: string | undefined, videoUrl: string): string | undefined {
  const imageUrls = post.attachments
    ?.filter((attachment) => attachment.media_type === 'IMAGE' || attachment.type?.startsWith('image/'))
    .flatMap((attachment) => [attachment.storage_url, attachment.url]) ?? [];

  return [...imageUrls, post.thumbnail_url, post.image_url, imageUrl].find(
    (url) => typeof url === 'string' && url.length > 0 && url !== videoUrl && !isHlsSource(url),
  );
}

interface PostAreaProps {
  post: Post;
  textRef: React.RefObject<HTMLParagraphElement | null>;
  hasGradient: boolean;
  imageUrl: string | undefined;
  isChecked: boolean;
  platform: string;
  onDispatch: (eventName: string, extra?: Record<string, unknown>) => void;
  onSetIsChecked: (value: boolean) => void;
  onSetShowClassifyModal: (value: boolean) => void;
  CLASSIFICATION_EVENTS: any;
  lineHeight: number;
  maxLines: number;
}

export const PostArea: React.FC<PostAreaProps> = ({
  post,
  textRef,
  hasGradient,
  imageUrl,
  isChecked,
  platform,
  onDispatch,
  onSetIsChecked,
  onSetShowClassifyModal,
  CLASSIFICATION_EVENTS,
  lineHeight,
  maxLines,
}) => {
  const isUnsupportedMedia = post.features?.includes('UNSUPPORTED_MEDIA') ?? false;
  const isInboxShare = post.features?.includes('INBOX_SHARE') ?? false;
  const sharedUrl = post.text?.trim() || post.attachments?.[0]?.url?.trim() || '';
  const hasMediaWarning = isUnsupportedMedia || isInboxShare;
  const { t } = useTranslation();
  const isEncryptedConversation = (post.features ?? []).some(
    (feature) => feature.toUpperCase() === 'ENCRYPTED',
  );
  const isAudioPost = Boolean(resolveAudioUrl(post));
  const renderedTextHtml = toSafeMarkdownHtml(post.text ?? '', isAudioPost ? post.post_url : undefined);
  const hasTitle = Boolean(post.title?.trim());
  const renderedTitleHtml = hasTitle ? toSafeMarkdownHtml(post.title!.trim()) : '';
  const imageTextHtml = parseImageText(post.computer_vision, t('cardPosts.computerVisionHeader'));
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [failedImageUrl, setFailedImageUrl] = useState<string>();
  const [failedHlsSource, setFailedHlsSource] = useState<string>();
  const videoUrl = resolveVideoUrl(post);
  const videoMimeType = resolveVideoMimeType(post);
  const hlsVideoUrl = [videoUrl, imageUrl].find(
    (url) => typeof url === 'string' && isHlsSource(url, url === videoUrl ? videoMimeType : undefined),
  );
  const thumbnailUrl = hlsVideoUrl ? resolveHlsThumbnail(post, imageUrl, hlsVideoUrl) : imageUrl;
  const imageSrc = failedImageUrl === thumbnailUrl ? fallbackImage : thumbnailUrl;
  const showHlsVideo = hlsVideoUrl && (!thumbnailUrl || failedImageUrl === thumbnailUrl);
  const hlsSourceKey = JSON.stringify([hlsVideoUrl, videoMimeType]);
  const showHlsFallback = showHlsVideo && failedHlsSource === hlsSourceKey;

  const renderThumbnail = () => {
    if (!thumbnailUrl && !hlsVideoUrl) return null;

    return (
      <div className="classification-card__image-wrapper">
        {showHlsVideo && !showHlsFallback ? (
          <HlsVideoPreview
            key={hlsSourceKey}
            src={hlsVideoUrl}
            mimeType={videoMimeType}
            alt={t('classificationCard.postImageAlt')}
            onFailure={() => setFailedHlsSource(hlsSourceKey)}
          />
        ) : (
          <img
            src={showHlsFallback ? fallbackImage : imageSrc}
            decoding="async"
            loading="lazy"
            fetchPriority="low"
            alt={t('classificationCard.postImageAlt')}
            className="classification-card__post-image"
            onError={() => setFailedImageUrl(thumbnailUrl)}
          />
        )}
      </div>
    );
  };

  const renderMediaWarning = () => (
    <div className="classification-card__media-warning-wrapper">
      {isInboxShare && sharedUrl && (
        <a
          className="classification-card__inbox-share-link"
          href={sharedUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {sharedUrl}
        </a>
      )}
      
      <div
        className={`classification-card__media-warning${isUnsupportedMedia ? '' : ' classification-card__media-warning--inbox-share'}`}
      >
        <span className="classification-card__media-warning-icon" aria-hidden="true">
          <BdsIcon theme="outline" name="warning" size="medium" />
        </span>
        <div className="classification-card__media-warning-content">
          
          {isUnsupportedMedia && (
            <strong className="classification-card__media-warning-title">
              {t('cardPosts.unsupportedMediaTitle')}
            </strong>
          )}
          
          <p className="classification-card__post-text classification-card__media-warning-text">
            {isUnsupportedMedia 
              ? t('cardPosts.unsupportedMediaDesc') 
              : t('cardPosts.inboxShareDesc')
            }
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="classification-card__post-area">
        {isEncryptedConversation && (
          <div className="classification-card__text-image-row">
            <div className="classification-card__encrypted-warning" role="note" aria-live="polite">
              <div className="classification-card__encrypted-warning-content">
                <span className="classification-card__encrypted-warning-icon" aria-hidden="true">
                  <BdsIcon theme="outline" name="warning" size="medium" />
                </span>
                <p className="classification-card__encrypted-warning-text">
                  {t('cardPosts.encryptedWarningStart')}
                  <strong>{t('cardPosts.encryptedWarningConnections')}</strong>
                  {t('cardPosts.encryptedWarningEnd')}
                </p>
              </div>
            </div>

            {renderThumbnail()}
          </div>
        )}
        {!isEncryptedConversation && hasMediaWarning && (
          renderMediaWarning()
        )}
        {!isEncryptedConversation && !hasMediaWarning && (
          <>
            <div className="classification-card__text-image-row">
              <div
                className={`classification-card__text-wrapper${hasGradient ? ' classification-card__text-wrapper--gradient' : ''}`}
              >
                {hasTitle && (
                  <p
                    className="classification-card__title"
                    style={{ maxHeight: `${lineHeight * maxLines}px` }}
                    dangerouslySetInnerHTML={{ __html: renderedTitleHtml }}
                  />
                )}
                <p
                  ref={textRef}
                  className="classification-card__post-text"
                  style={{ maxHeight: `${lineHeight * maxLines}px` }}
                  dangerouslySetInnerHTML={{ __html: renderedTextHtml }}
                />
              </div>

              {renderThumbnail()}
            </div>

            {imageTextHtml && (
              <div className="classification-card__image-text" dangerouslySetInnerHTML={{ __html: imageTextHtml }} />
            )}
          </>
        )}

      <div className="classification-card__actions-row">
        <div className="classification-card__actions-left">
          {platform === 'twitter' && (
          <BdsButton variant="solid" color="content" size="short" onClick={() => setShowReplyModal(true)}>
            {t('classificationCard.reply')}
          </BdsButton>
          )}
          <BdsTooltip position="top-left" tooltip-text={t('classificationCard.tooltipFilterSimilar')}>
            <BdsButton variant="solid" color="content" size="short" onClick={() => onDispatch(CLASSIFICATION_EVENTS.SIMILAR)}>
              {t('classificationCard.similar')}
            </BdsButton>
          </BdsTooltip>
          <BdsTooltip position="top-left" tooltip-text={t('classificationCard.tooltipReadAll')}>
            <BdsButton variant="solid" color="content" size="short" onClick={() => { onDispatch(CLASSIFICATION_EVENTS.READ_ALL); onSetShowClassifyModal(true); }}>
              {t('classificationCard.readAll')}
            </BdsButton>
          </BdsTooltip>
          {platform === 'youtube' && (
            <BdsTooltip position="top-center" tooltip-text={t('classificationCard.tooltipYoutubeInfo')}>
              <BdsIcon name="info" size="medium" />
            </BdsTooltip>
          )}
        </div>

        <div className="classification-card__actions-right">
          <BdsButton
            variant="outline"
            color="content"
            size="short"
            onClick={() => {
              if (post.post_url) window.open(post.post_url, '_blank', 'noopener,noreferrer');
            }}
          >
            {t('classificationCard.accessOriginal')}
          </BdsButton>
          <BdsTooltip position="top-left" tooltip-text={t('classificationCard.tooltipCopyLink')}>
            <button
              className="classification-card__custom-btn classification-card__custom-btn--outline-content"
              onClick={() => onDispatch(CLASSIFICATION_EVENTS.COPY)}
              aria-label={t('classificationCard.tooltipCopyLink')}
            >
              <BdsIcon name="link" size="medium" />
              <BdsTypo
                bold="bold"
                line-height="none"
                variant="fs-14"
              >{t('classificationCard.copyLink')}</BdsTypo>
            </button>
          </BdsTooltip>
          <BdsTooltip position="top-left" tooltip-text={t('classificationCard.tooltipCopyId')}>
            <button
              className="classification-card__custom-btn classification-card__custom-btn--outline-content"
              aria-label={t('classificationCard.tooltipCopyId')}
              onClick={() => onDispatch(CLASSIFICATION_EVENTS.COPY_ID)}
            >
              <BdsIcon name="copy" size="medium" />
              <BdsTypo
                bold="bold"
                line-height="none"
                variant="fs-14"
              >{t('classificationCard.copyId')}</BdsTypo>
            </button>
          </BdsTooltip>
            {isChecked ? (
              <button
                key="btn-checked-active"
                className="classification-card__btn-checked classification-card__btn-checked--active"
                onClick={() => {
                  onSetIsChecked(false);
                  onDispatch(CLASSIFICATION_EVENTS.MARK_CHECKED, { value: false });
                }}
              >
                <BdsIcon name="verified" color="white" size="small" />
                {t('classificationCard.tooltipChecked')}
              </button>
            ) : (
              <button
                key="btn-checked-inactive"
                className="classification-card__btn-checked"
                color="success"
                onClick={() => {
                  onSetIsChecked(true);
                  onDispatch(CLASSIFICATION_EVENTS.MARK_CHECKED, { value: true });
                }}
              >
                {t('classificationCard.markAsChecked')}
              </button>
            )}
        </div>
      </div>
    </div>

      {showReplyModal && (
        <ReplyModal
          platform={platform}
          onClose={() => setShowReplyModal(false)}
          onSend={(pageId, text) => {
            onDispatch(CLASSIFICATION_EVENTS.REPLY, { pageId, text });
          }}
        />
      )}
    </>
  );
};
